import React, { useState } from 'react';
import {
	CheckboxCheckedGold,
	CheckboxUncheckedSmall,
	CommunicationCenterIcon,
	Instagram,
	Messenger,
	PhoneSound,
	Profile,
	RadioButtonEmptyWhite,
	RadioButtonSelectedGold,
	WebsiteAvailable,
	WebsiteUnavailable
} from '@/resources/assets/images';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import {
	BusinessQuery,
	Filter,
	FilterQueryType,
	FilterType
} from '@/modules/dashboard/dashboardTypes';
import { sendAnalytics } from '@/services/analyticsService';
import omit from 'lodash/omit';
import { isQueryDifferent } from '../src/helpers/isQueryDifferent';
import SearchTabFiltersListItemHeader from '../SearchTabFiltersListItemHeader';
import { useSearchTabContext } from '../SearchTabContext';
import './SearchTabContactInfo.scss';

type SearchTabContactInfoProps = {
	onFilterChange: (query: BusinessQuery) => void;
	onGetBusiness: (query: BusinessQuery) => void;
};

const SearchTabContactInfo = ({
	onFilterChange,
	onGetBusiness
}: SearchTabContactInfoProps) => {
	const [query, user] = useSelector(
		(state: RootState) => [
			state.dashboard.get('query').toJS(),
			state.user.getIn(['authorize', 'user']).toJS()
		],
		shallowEqual
	);

	const [contactInfoQuery, setContactInfoQuery] = useState<
		BusinessQuery | {}
	>({});

	const { selectedListItem } = useSearchTabContext();

	const clearFilters = () => {
		const queryNew = { ...query };

		delete queryNew.includeEmailsYes;
		delete queryNew.includeFBMessenger;

		delete queryNew.includeFBMessenger;
		delete queryNew.includePhoneNumber;
		delete queryNew.includeIGMessenger;
		delete queryNew.includeWebsitesYes;
		delete queryNew.includeWebsitesNo;

		queryNew.skip = 0;

		sendAnalytics({
			type: 'track',
			userId: user.email,
			properties: {
				query: queryNew
			},
			eventName: 'User cleared contact info filters'
		});

		onFilterChange(queryNew);

		const queryChanged = isQueryDifferent(queryNew);

		if (queryChanged) {
			onGetBusiness(queryNew);
		}

		setContactInfoQuery({});
	};

	const queryChange = (filter: Filter) => {
		const queryNew = { ...query, user };

		if (filter.value === 'includeEmailsYes') {
			if (filter.type === FilterType.INCLUDES) {
				setContactInfoQuery({
					...contactInfoQuery,
					includeEmailsYes: true
				});
				queryNew.includeEmailsYes = true;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has email',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				setContactInfoQuery(omit(contactInfoQuery, 'includeEmailsYes'));
				delete queryNew.includeEmailsYes;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has email',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'includeFBMessenger') {
			if (filter.type === FilterType.INCLUDES) {
				setContactInfoQuery({
					...contactInfoQuery,
					includeFBMessenger: true
				});
				queryNew.includeFBMessenger = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has Facebook Messenger',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				setContactInfoQuery(
					omit(contactInfoQuery, 'includeFBMessenger')
				);
				delete queryNew.includeFBMessenger;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has Facebook Messenger',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'includeIGMessenger') {
			if (filter.type === FilterType.INCLUDES) {
				setContactInfoQuery({
					...contactInfoQuery,
					includeIGMessenger: true
				});
				queryNew.includeIGMessenger = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has Instagram Messenger',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				setContactInfoQuery(
					omit(contactInfoQuery, 'includeIGMessenger')
				);
				delete queryNew.includeIGMessenger;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has Instagram Messenger',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'includePhoneNumber') {
			if (filter.type === FilterType.INCLUDES) {
				setContactInfoQuery({
					...contactInfoQuery,
					includePhoneNumber: true
				});
				queryNew.includePhoneNumber = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has Phone Number',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				setContactInfoQuery(
					omit(contactInfoQuery, 'includePhoneNumber')
				);
				delete queryNew.includePhoneNumber;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has Phone Number',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'includeWebsitesYes') {
			setContactInfoQuery(
				omit(
					{ ...contactInfoQuery, includeWebsitesYes: true },
					'includeWebsitesNo'
				)
			);
			queryNew.includeWebsitesYes = true;
			delete queryNew.includeWebsitesNo;
			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: 'User changed filters',
					properties: {
						filterChanged: 'Has Website',
						value: 'Included',
						query: queryNew
					}
				});
			}
		}

		if (filter.value === 'includeWebsitesNo') {
			setContactInfoQuery(
				omit(
					{ ...contactInfoQuery, includeWebsitesNo: true },
					'includeWebsitesYes'
				)
			);
			queryNew.includeWebsitesNo = true;
			delete queryNew.includeWebsitesYes;
			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: 'User changed filters',
					properties: {
						filterChanged: 'Does not have Website',
						value: 'Included',
						query: queryNew
					}
				});
			}
		}

		queryNew.limit = 50;

		queryNew.skip = 0;

		queryNew.queryType = FilterQueryType.FILTER;

		onFilterChange(queryNew);
		onGetBusiness(queryNew);
	};

	const filtersCount = () => {
		let count = 0;

		if (query?.includeEmailsYes) {
			count++;
		}

		if (query?.includeFBMessenger) {
			count++;
		}

		if (query?.includeIGMessenger) {
			count++;
		}

		if (query?.includePhoneNumber) {
			count++;
		}

		if (query?.includeWebsitesYes) {
			count++;
		}

		if (query?.includeWebsitesNo) {
			count++;
		}

		return count;
	};

	return (
		<div
			className={`search-tab-filters-list-item ${selectedListItem === 'Contact Info' ? 'opened' : ''} ${filtersCount() > 0 ? 'filter-selected' : ''} `}
		>
			<SearchTabFiltersListItemHeader
				filterName="Contact Info"
				filterCount={filtersCount()}
				onClick={clearFilters}
				displayClearFiltersButton={filtersCount() > 0}
				icon={
					<Profile className="search-tab-filter-list-item-icon profile" />
				}
			/>
			<div className="search-tab-filters-list-item-dropdown">
				<div className="search-tab-filters-list-item-dropdown-divider" />
				<div className="search-tab-filters-list-item-dropdown-filter-group first">
					<h1 className="search-tab-filters-list-item-dropdown-filter-group-header">
						Contact Information
						<span>*select all applied</span>
					</h1>
					<div className="search-tab-filters-list-item-dropdown-filter-group-contact-info">
						<div className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item">
							{query.includeEmailsYes ? (
								<CheckboxCheckedGold
									className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-checked"
									onClick={() =>
										queryChange({
											type: FilterType.EXCLUDES,
											value: 'includeEmailsYes'
										})
									}
								/>
							) : (
								<CheckboxUncheckedSmall
									className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-unchecked"
									onClick={() =>
										queryChange({
											type: FilterType.INCLUDES,
											value: 'includeEmailsYes'
										})
									}
								/>
							)}
							<span className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner">
								<CommunicationCenterIcon
									width={12}
									height={12}
								/>
								Email
							</span>
						</div>
						<div className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item">
							{query.includeFBMessenger ? (
								<CheckboxCheckedGold
									className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-checked"
									onClick={() =>
										queryChange({
											type: FilterType.EXCLUDES,
											value: 'includeFBMessenger'
										})
									}
								/>
							) : (
								<CheckboxUncheckedSmall
									className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-unchecked"
									onClick={() =>
										queryChange({
											type: FilterType.INCLUDES,
											value: 'includeFBMessenger'
										})
									}
								/>
							)}
							<span className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner">
								<Messenger width={12} height={12} />
								Facebook Messenger
							</span>
						</div>
						<div className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item">
							{query.includeIGMessenger ? (
								<CheckboxCheckedGold
									className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-checked"
									onClick={() =>
										queryChange({
											type: FilterType.EXCLUDES,
											value: 'includeIGMessenger'
										})
									}
								/>
							) : (
								<CheckboxUncheckedSmall
									className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-unchecked"
									onClick={() =>
										queryChange({
											type: FilterType.INCLUDES,
											value: 'includeIGMessenger'
										})
									}
								/>
							)}
							<span className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner">
								<Instagram width={12} height={12} />
								Instagram Messenger
							</span>
						</div>
						<div className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item">
							{query.includePhoneNumber ? (
								<CheckboxCheckedGold
									className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-checked"
									onClick={() =>
										queryChange({
											type: FilterType.EXCLUDES,
											value: 'includePhoneNumber'
										})
									}
								/>
							) : (
								<CheckboxUncheckedSmall
									className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-unchecked"
									onClick={() =>
										queryChange({
											type: FilterType.INCLUDES,
											value: 'includePhoneNumber'
										})
									}
								/>
							)}
							<span className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner">
								<PhoneSound className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner-icon phone" />
								Phone Number
							</span>
						</div>
					</div>
				</div>
				<div className="search-tab-filters-list-item-dropdown-divider" />
				<div className="search-tab-filters-list-item-dropdown-filter-group">
					<h1 className="search-tab-filters-list-item-dropdown-filter-group-header mt-2">
						Website
					</h1>
					<div className="search-tab-filters-list-item-dropdown-filter-group-contact-info">
						<div className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item radio">
							{query.includeWebsitesYes ? (
								<RadioButtonSelectedGold />
							) : (
								<RadioButtonEmptyWhite
									onClick={() =>
										queryChange({
											type: FilterType.INCLUDES,
											value: 'includeWebsitesYes'
										})
									}
								/>
							)}
							<span className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner">
								<WebsiteAvailable className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner-icon website" />
								Website
							</span>
						</div>
						<div className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item radio">
							{query.includeWebsitesNo ? (
								<RadioButtonSelectedGold />
							) : (
								<RadioButtonEmptyWhite
									onClick={() =>
										queryChange({
											type: FilterType.INCLUDES,
											value: 'includeWebsitesNo'
										})
									}
								/>
							)}
							<span className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner">
								<WebsiteUnavailable className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner-icon website" />
								No Website
							</span>
						</div>
					</div>
				</div>
				<div className="search-tab-filters-list-item-dropdown-divider" />
			</div>
		</div>
	);
};

export default SearchTabContactInfo;
